import validator from 'validator';

export const boldifyVariableValue = (text: string = '') =>
  text.replace(/{{.*?}}/g, (val) => `<strong>${val}</strong>`);

/**
 * Basic email format validation regex (RFC 5322).
 * Note: Use isEmail() for validation with security checks.
 */
export const EMAIL_REGEX =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/**
 * Dangerous characters blocked in email addresses:
 * control chars, zero-width chars, line/paragraph separators.
 */
const DANGEROUS_EMAIL_CHARS =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u001F\u007F-\u009F\u200B-\u200D\u2028\u2029\uFEFF]/;

/**
 * Checks if an email contains dangerous characters.
 */
export function hasDangerousEmailChars(email: string): boolean {
  return DANGEROUS_EMAIL_CHARS.test(String(email));
}

/**
 * Validates email addresses with security checks and international character support.
 *
 * - Blocks dangerous characters (zero-width, control chars)
 * - Supports Unicode/international emails per RFC 6531 (e.g., josé@example.com)
 * - Max 320 characters per RFC 5321
 *
 * @param email - The email address to validate
 * @param options - Optional validator.js options
 * @returns true if email is valid and secure, false otherwise
 */
export function isEmail(
  email: string,
  options?: validator.IsEmailOptions
): boolean {
  const emailStr = String(email);

  if (hasDangerousEmailChars(emailStr)) {
    return false;
  }

  if (emailStr.length > 320) {
    return false;
  }

  return validator.isEmail(emailStr, {
    allow_utf8_local_part: true,
    ...options,
  });
}

export type EmailValidationErrors = {
  email?: 'Required' | 'Invalid email address';
};

/**
 * Validates email input for forms.
 */
export const emailValidator = ({
  email,
}: {
  email: string;
}): EmailValidationErrors => {
  const errors: EmailValidationErrors = {};
  if (!email) {
    errors.email = 'Required';
  } else if (!isEmail(email)) {
    errors.email = 'Invalid email address';
  }
  return errors;
};
